/* eslint-disable react/jsx-no-comment-textnodes */
import type { ReactNode, ReactElement } from 'react';

import { SnackbarProvider, MaterialDesignContent } from 'notistack';
// @mui
import { alpha, useTheme, styled } from '@mui/material/styles';
import { Box, GlobalStyles } from '@mui/material';
//
import { Icon, type IconifyIcon } from '@iconify/react';
import infoFill from '@iconify/icons-eva/info-fill';
import alertCircleFill from '@iconify/icons-eva/alert-circle-fill';
import alertTriangleFill from '@iconify/icons-eva/alert-triangle-fill';
import checkmarkCircle2Fill from '@iconify/icons-eva/checkmark-circle-2-fill';

export interface NotistackProviderProps {
  children?: ReactNode;
  [key: string]: any;
}

export interface SnackbarIconProps {
  icon: IconifyIcon;
  color?: string;
  [key: string]: any;
}

// ----------------------------------------------------------------------

// Styled content for notistack v3 - white background with colored icons
const StyledMaterialDesignContent = styled(MaterialDesignContent)(
  ({ theme }) => ({
    '&.notistack-MuiContent-success': {
      color: theme.palette.text.primary,
      backgroundColor: theme.palette.background.paper,
      // @ts-ignore legacy boundary
      boxShadow: theme.customShadows?.z8,
    },
    '&.notistack-MuiContent-error': {
      color: theme.palette.text.primary,
      backgroundColor: theme.palette.background.paper,
      // @ts-ignore legacy boundary
      boxShadow: theme.customShadows?.z8,
    },
    '&.notistack-MuiContent-warning': {
      color: theme.palette.text.primary,
      backgroundColor: theme.palette.background.paper,
      // @ts-ignore legacy boundary
      boxShadow: theme.customShadows?.z8,
    },
    '&.notistack-MuiContent-info': {
      color: theme.palette.text.primary,
      backgroundColor: theme.palette.background.paper,
      // @ts-ignore legacy boundary
      boxShadow: theme.customShadows?.z8,
    },
    '&.notistack-MuiContent-default': {
      color: theme.palette.text.primary,
      backgroundColor: theme.palette.background.paper,
      // @ts-ignore legacy boundary
      boxShadow: theme.customShadows?.z8,
    },
  })
);

function SnackbarStyles() {
  const theme = useTheme();

  return (
    <GlobalStyles
      styles={{
        '#root': {
          '& .notistack-SnackbarContainer': {
            '& .notistack-Snackbar': {
              width: '100%',
            },
          },
          '& .notistack-MuiContent': {
            padding: theme.spacing(1.5),
            margin: theme.spacing(0.25, 0),
            borderRadius: theme.shape.borderRadius,
          },
          '& .SnackbarItem-message': {
            padding: '0 !important',
            fontWeight: theme.typography.fontWeightMedium,
          },
          '& .SnackbarItem-action': {
            marginRight: 0,
            color: theme.palette.action.active,
            '& svg': { width: 20, height: 20 },
          },
        },
      }}
    />
  );
}

export default function NotistackProvider({
  children,
}: NotistackProviderProps) {
  return (
    <>
      <SnackbarStyles />

      <SnackbarProvider
        dense
        maxSnack={5}
        // preventDuplicate
        autoHideDuration={3000}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        Components={{
          success: StyledMaterialDesignContent,
          error: StyledMaterialDesignContent,
          warning: StyledMaterialDesignContent,
          info: StyledMaterialDesignContent,
          default: StyledMaterialDesignContent,
        }}
        iconVariant={{
          success: <SnackbarIcon icon={checkmarkCircle2Fill} color="success" />,
          error: <SnackbarIcon icon={infoFill} color="error" />,
          warning: <SnackbarIcon icon={alertTriangleFill} color="warning" />,
          info: <SnackbarIcon icon={alertCircleFill} color="info" />,
        }}
      >
        {children}
      </SnackbarProvider>
    </>
  );
}

// ----------------------------------------------------------------------

function SnackbarIcon({ icon, color }: SnackbarIconProps) {
  return (
    <Box
      component="span"
      sx={{
        mr: 1.5,
        width: 40,
        height: 40,
        display: 'flex',
        borderRadius: 1.5,
        alignItems: 'center',
        justifyContent: 'center',
        color: `${color}.main`,
        bgcolor: (theme) => alpha(theme.palette[color].main, 0.16),
      }}
    >
      <Icon icon={icon} width={24} height={24} />
    </Box>
  );
}
